import api from "..";
import { playListPassword, playListUsername } from "../../../state";
import {
  convertItemsToTilesChannelCategory,
  chunkArray,
} from "../formatters/ItemFormatter";

const cache = new Map<string, Promise<any>>();
const leftoverTiles = new Map<string, any[]>();

// Enhanced API call wrapper for categories
const safeApiGetCategories = async (url, headers = {}) => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      console.log(`API attempt ${attempt}/${MAX_RETRIES}: ${url}`);

      const response = await api._get(url, { headers });

      // Validate response is not HTML
      if (typeof response === "string" && response.trim().startsWith("<")) {
        throw new Error(`Received HTML instead of JSON for categories`);
      }

      console.log(`API call successful`);
      return response;
    } catch (error) {
      console.warn(`API attempt ${attempt} failed:`, error.message);

      if (
        error.message.includes("Unexpected token <") ||
        error.message.includes("JSON") ||
        error.message.includes("HTML instead of JSON")
      ) {
        console.error(
          `JSON parsing error for categories - server returned HTML`
        );
      }

      if (attempt === MAX_RETRIES) {
        console.error(
          `All attempts failed for categories, returning empty array`
        );
        return [];
      }

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
    }
  }

  return [];
};

export default function (filter: string) {
  return async (pageIndex: number): Promise<any> => {
    const username = playListUsername();
    const password = playListPassword();
    const apiIdentifier = api.getIdentifier ? api.getIdentifier() : "default";
    const cacheKey = `${apiIdentifier}_${username}_vod_categories`;
    const tileKey = `${filter}_${apiIdentifier}_${username}_vod_categories`;

    const finalUrl = `/player_api.php?username=${username}&password=${password}&action=get_vod_categories`;

    // Check cache first
    if (cache.has(cacheKey)) {
      console.log(`Using cached data for VOD categories`);
      return cache.get(cacheKey);
    }

    console.log(`Making VOD categories API call with URL: ${finalUrl}`);

    const result = safeApiGetCategories(finalUrl)
      .then((channels) => {
        console.log(
          `VOD categories API call successful, processing ${channels?.length || 0} categories`
        );

        // Ensure channels is always an array
        let results = [];

        if (Array.isArray(channels)) {
          results = channels;
        } else if (channels && typeof channels === "object") {
          // Handle different response structures
          if (Array.isArray(channels.data)) {
            results = channels.data;
          } else if (Array.isArray(channels.categories)) {
            results = channels.categories;
          } else if (Array.isArray(channels.results)) {
            results = channels.results;
          } else {
            console.warn(
              "Unexpected VOD categories response structure:",
              typeof channels,
              channels
            );
            results = [];
          }
        } else {
          console.warn(
            "VOD categories response is not an object or array:",
            typeof channels,
            channels
          );
          results = [];
        }

        console.log(
          `Processed VOD categories: ${results.length} valid categories found`
        );

        // Add "Last Added" category at the beginning
        const lastAddedCategory = {
          category_id: "last_added",
          category_name: "Last Added",
          parent_id: 0,
        };

        const categoriesWithLastAdded = [lastAddedCategory, ...results];

        const existingTiles = leftoverTiles.get(tileKey) || [];
        const tiles = existingTiles.concat(
          convertItemsToTilesChannelCategory(categoriesWithLastAdded)
        );

        const chunks = chunkArray(tiles);
        if (chunks[chunks.length - 1].length < 7) {
          leftoverTiles.set(tileKey, chunks.pop() || []);
        } else {
          leftoverTiles.delete(tileKey);
        }

        console.log(
          `VOD categories processed: ${tiles.length} total tiles created`
        );
        return tiles;
      })
      .catch((error) => {
        console.error(`VOD categories API call failed:`, error);
        cache.delete(cacheKey);

        // Handle JSON parsing errors
        if (
          error.message.includes("Unexpected token <") ||
          error.message.includes("JSON")
        ) {
          console.error(
            `JSON parsing error for VOD categories - API returned HTML`
          );
        }

        // Return empty array instead of throwing to prevent app crash
        return [];
      });

    cache.set(cacheKey, result);
    return result;
  };
}
